<script lang="ts">
    import { shuffle as shuffleArray } from "es-toolkit/compat";
    import type { Snippet } from "svelte";
    import type { ElearningAnswer, ElearningQuestion, ElearningTest } from "../elearning.js";
    import { createBlobUrl, getLetter } from "../elearning.js";
    import { MediaImage, MediaVideo, VideoQuestion } from "./index.js";

    interface Props {
        test: ElearningTest;
        question: ElearningQuestion;
        submitted?: boolean;
        shuffle?: boolean;
        submit?: CallableFunction;
        next?: CallableFunction;
        canNext?: () => boolean;
        selected?: ElearningAnswer | null;
        renderAnswer?: boolean;
        submitAction?: Snippet | null;
        nextAction?: Snippet | null;
    }

    let {
        test,
        question,
        submitted = false,
        shuffle = true,
        submit = () => {},
        next = () => {},
        canNext = () => false,
        selected = $bindable(null),
        renderAnswer = false,
        submitAction = null,
        nextAction = null,
    }: Props = $props();

    let answers: Array<ElearningAnswer> = $state([]);

    function getAnswers(question: ElearningQuestion, randomOrder: boolean) {
        return randomOrder ? shuffleArray(question.answers) : question.answers;
    }

    function render(question: ElearningQuestion) {
        if (getRenderType("answer") === 2) {
            renderAnswer = false;
            return;
        }

        renderAnswer = true;
    }

    function getRenderType(entity: "question" | "answer"): number {
        const renderTest = test[`${entity}_render_type`];
        const renderQuestion = question[`${entity}_render_type`];

        if (test.force_render) {
            return renderTest;
        }

        if (renderTest === 1) {
            return renderQuestion;
        }

        return renderTest;
    }

    function onsubmit(event: Event) {
        event.preventDefault();
        submit?.();
    }

    $effect(() => render(question));
    $effect(() => {
        answers = getAnswers(question, shuffle);
    });
</script>


    <form enctype="multipart/form-data" {onsubmit}>
        <div class="border-secondary grid min-h-80 place-items-center gap-8 border-2 p-6 text-center">
            <h3 class="max-w-3xl text-3xl font-bold">
                <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                {@html question.content}
            </h3>

            {#if question.file}
                {@const src = question.file.source}
                {@const type = question.file.mime_type}

                {#if question.file.type === "image"}
                    <MediaImage {src} alt={question.title} />
                {:else if getRenderType("answer") === 2}
                    {#if !renderAnswer}
                        <VideoQuestion file={question.file} {type} bind:ended={renderAnswer} frameTimeInSeconds={4} />
                    {/if}
                {:else}
                    <MediaVideo file={question.file} {type} />
                {/if}
            {/if}
        </div>

        {#if renderAnswer}
            <ul class="my-12 flex flex-col gap-4">
                {#each answers as answer, index (answer.id)}
                    {@const active = selected?.id === answer.id}

                    <li>
                        <label
                            class="flex items-center gap-6 border p-4"
                            class:cursor-pointer={!submitted}
                            class:border-transparent={!active || submitted}
                            class:border-primary={active && !submitted}
                            class:text-primary={active && !submitted}
                            class:hover:bg-answer-selected={!submitted}
                            class:bg-answer={!submitted}
                            class:bg-answer-selected={!submitted && active}
                            class:text-white={submitted && active}
                            class:text-answer-wrong-writing={submitted && !answer.is_correct && !active}
                            class:text-answer-correct-writing={submitted && answer.is_correct && !active}
                            class:bg-answer-correct={submitted && answer.is_correct && !active}
                            class:bg-answer-wrong={submitted && !answer.is_correct && !active}
                            class:bg-answer-correct-selected={submitted && answer.is_correct && active}
                            class:bg-answer-wrong-selected={submitted && !answer.is_correct && active}
                        >
                            <input
                                hidden
                                type="radio"
                                name="answer"
                                value={answer}
                                bind:group={selected}
                                required
                                disabled={submitted}
                            />
                            <span
                                class="grid aspect-square h-10 place-items-center text-lg"
                                class:text-white={active}
                                class:text-answer-wrong-writing={submitted && !answer.is_correct && !active}
                                class:text-answer-correct-writing={submitted && answer.is_correct && !active}
                                class:bg-answer-letter={!active && !submitted}
                                class:bg-answer-letter-selected={active && !submitted}
                                class:bg-answer-letter-correct={submitted && answer.is_correct && !active}
                                class:bg-answer-letter-wrong={submitted && !answer.is_correct && !active}
                                class:bg-answer-letter-correct-selected={submitted && answer.is_correct && active}
                                class:bg-answer-letter-wrong-selected={submitted && !answer.is_correct && active}
                            >
                                {#if submitted}
                                    {#if answer.is_correct}
                                        <svg
                                            class="h-8 fill-current"
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="24"
                                            viewBox="0 -960 960 960"
                                            width="24"
                                        >
                                            <path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
                                        </svg>
                                    {:else}
                                        <svg
                                            class="h-8 fill-current"
                                            xmlns="http://www.w3.org/2000/svg"
                                            height="24"
                                            viewBox="0 -960 960 960"
                                            width="24"
                                        >
                                            <path
                                                d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"
                                            />
                                        </svg>
                                    {/if}
                                {:else}
                                    <strong>{getLetter(index, "?").toUpperCase()}</strong>
                                {/if}
                            </span>
                            <div class="flex flex-col items-center gap-8">
                                {#if answer.content}
                                    <span class="max-w-3xl">
                                        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                                        {@html answer.content}
                                    </span>
                                {/if}

                                {#if answer.file}
                                    {@const src = answer.file.source}
                                    {@const type = answer.file.mime_type}

                                    {#await createBlobUrl(src)}
                                        <p>Obrázek se načítá...</p>
                                    {:then src}
                                        {#if answer.file.type === "image"}
                                            <img src={answer.file.source} alt={answer.title} class="h-40 w-auto" />
                                        {:else if answer.file.type === "video"}
                                            <video class="h-40 w-auto" autoplay controls>
                                                <source {src} {type} />
                                            </video>
                                        {/if}
                                    {/await}
                                {/if}
                            </div>
                        </label>
                    </li>
                {/each}
            </ul>
        {:else}
            <p class="my-6 italic">Po odehrání celého videa se zobrazí odpovědi...</p>
        {/if}

        {#if submitted}
            {#if nextAction}
                {@render nextAction()}
            {:else}
                <button
                    class="bg-primary hover:bg-primary/80 disabled:bg-primary/60 w-40 cursor-pointer px-6 py-2 font-bold text-white"
                    type="button"
                    onclick={next}
                    disabled={!canNext()}
                >
                    Další
                </button>
            {/if}
        {:else if submitAction}
            {@render submitAction()}
        {:else}
            <button
                class="bg-primary hover:bg-primary/80 disabled:bg-primary/60 w-40 cursor-pointer px-6 py-2 font-bold text-white"
                type="submit"
                disabled={selected === null}
            >
                Vyhodnotit
            </button>
        {/if}
    </form>

